import {
    Delete as DeleteIcon,
    Palette as PaletteIcon,
    TextFields as TypographyIcon,
    Upload as UploadIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Divider,
    Grid,
    IconButton,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import React, { useContext, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import ImageUpload, { Upload } from '../object-actions/forming/ImageUpload';
import FontSelector from '../theme/FontSelector';
import Logo from '../theme/Logo';
import { ThemeContext } from '../theme/ThemeContext';
import ThemeSwitcher from '../theme/ThemeSwitcher';

// Utility function to convert hex to RGB
const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

// Calculate relative luminance (0-1, where 0 is black and 1 is white)
const getLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0.5;

    const [r, g, b] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map(val => {
        return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Determine if a color is "light" (true) or "dark" (false) based on luminance threshold
const isLight = (hex: string, threshold: number = 0.5): boolean => {
    return getLuminance(hex) > threshold;
};

// Adjust color to be darker (reduce luminance)
const darkenColor = (hex: string, amount: number = 0.3): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const factor = 1 - amount;
    const r = Math.max(0, Math.min(255, Math.round(rgb.r * factor)));
    const g = Math.max(0, Math.min(255, Math.round(rgb.g * factor)));
    const b = Math.max(0, Math.min(255, Math.round(rgb.b * factor)));

    return `#${[r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
};

// Adjust color to be lighter (increase luminance)
const lightenColor = (hex: string, amount: number = 0.3): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;

    const factor = 1 + amount;
    const r = Math.max(0, Math.min(255, Math.round(rgb.r + (255 - rgb.r) * (factor - 1))));
    const g = Math.max(0, Math.min(255, Math.round(rgb.g + (255 - rgb.g) * (factor - 1))));
    const b = Math.max(0, Math.min(255, Math.round(rgb.b + (255 - rgb.b) * (factor - 1))));

    return `#${[r, g, b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
};

const BrandingSettings: React.FC = () => {
    const { brandingSettings, updateBrandingSettings, resetBrandingSettings, updateLogo, updateOrganizationName, updateOrganizationShortName, updateLogoUrl } = useContext(ThemeContext);
    const { membership, organization } = useAppContext();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    // Check if user has admin or agent role
    const availableRoles = membership?.roles || [];
    if (!availableRoles.includes('admin') && !availableRoles.includes('agent')) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography variant="h6" color="error">
                    Access Denied
                </Typography>
                <Typography>
                    You need admin privileges to access branding settings.
                </Typography>
            </Box>
        );
    }

    const handleImageSelect = (selected: Upload, field_name: string, index: number) => {
        if (selected && selected.file) {
            setUploadedFile(selected.file as File);
            // Convert to base64 for storage
            const reader = new FileReader();
            reader.onload = () => {
                updateLogo(reader.result as string);
                setSnackbarMessage('Logo updated successfully!');
                setSnackbarOpen(true);
            };
            reader.onerror = () => {
                setSnackbarMessage('Error uploading logo');
                setSnackbarOpen(true);
            };
            reader.readAsDataURL(selected.file);
        }
    };

    const handleRemoveLogo = () => {
        setUploadedFile(null);
        updateLogo(null);
        setSnackbarMessage('Logo removed successfully!');
        setSnackbarOpen(true);
    };

    const handleColorChange = (path: string, value: string) => {
        // Deep clone the settings to avoid mutating the original
        const newSettings = JSON.parse(JSON.stringify(brandingSettings));
        const keys = path.split('.');
        let current: Record<string, any> = newSettings;

        // Navigate to the target property and update it
        for (let i = 0; i < keys.length - 1; i++) {
            current = current[keys[i]];
        }
        current[keys[keys.length - 1]] = value;

        // Apply automatic overrides for contrast issues
        const themeMode = keys[1]; // 'light' or 'dark'
        if (themeMode === 'light' || themeMode === 'dark') {
            const palette = newSettings.palette[themeMode];
            const background = palette.background?.default;
            const primary = palette.primary?.main;
            const secondary = palette.secondary?.main;

            if (background && primary && secondary) {
                const bgIsLight = isLight(background);
                const primaryIsLight = isLight(primary);
                const secondaryIsLight = isLight(secondary);

                // Check if both primary and secondary are on the same side as background
                const needsOverride = (bgIsLight && primaryIsLight && secondaryIsLight) ||
                    (!bgIsLight && !primaryIsLight && !secondaryIsLight);

                if (needsOverride) {
                    // Apply overrides: adjust colors for better contrast
                    if (bgIsLight) {
                        // Background is light, but primary/secondary are also light - darken them
                        if (primaryIsLight) {
                            newSettings.palette[themeMode].primary.main = darkenColor(primary, 0.4);
                        }
                        if (secondaryIsLight) {
                            newSettings.palette[themeMode].secondary.main = darkenColor(secondary, 0.4);
                        }
                        setSnackbarMessage('Colors adjusted for better contrast (darkened primary/secondary)');
                    } else {
                        // Background is dark, but primary/secondary are also dark - lighten them
                        if (!primaryIsLight) {
                            newSettings.palette[themeMode].primary.main = lightenColor(primary, 0.4);
                        }
                        if (!secondaryIsLight) {
                            newSettings.palette[themeMode].secondary.main = lightenColor(secondary, 0.4);
                        }
                        setSnackbarMessage('Colors adjusted for better contrast (lightened primary/secondary)');
                    }
                    setSnackbarOpen(true);
                }
            }
        }

        updateBrandingSettings(newSettings);
    };

    const handleOrganizationNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateOrganizationName(event.target.value);
    };

    const handleOrganizationShortNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateOrganizationShortName(event.target.value);
    };

    const handleLogoUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateLogoUrl(event.target.value);
    };


    // Check for contrast issues and return warning message if found
    const getContrastWarning = (themeMode: 'light' | 'dark'): string | null => {
        const palette = brandingSettings.palette[themeMode];
        const background = palette.background?.default;
        const primary = palette.primary?.main;
        const secondary = palette.secondary?.main;

        if (background && primary && secondary) {
            const bgIsLight = isLight(background);
            const primaryIsLight = isLight(primary);
            const secondaryIsLight = isLight(secondary);

            // Check if both primary and secondary are on the same side as background
            const hasIssue = (bgIsLight && primaryIsLight && secondaryIsLight) ||
                (!bgIsLight && !primaryIsLight && !secondaryIsLight);

            if (hasIssue) {
                return bgIsLight
                    ? 'Warning: Primary and secondary colors are too light for the light background. Colors will be automatically adjusted.'
                    : 'Warning: Primary and secondary colors are too dark for the dark background. Colors will be automatically adjusted.';
            }
        }
        return null;
    };

    const handleSave = () => {
        setSnackbarMessage('Settings saved successfully!');
        setSnackbarOpen(true);
    };

    const handleReset = () => {
        resetBrandingSettings();
        setSnackbarMessage('Settings reset to defaults!');
        setSnackbarOpen(true);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom>
                {organization?.name ? `${organization.name} - Branding Settings` : 'Branding Settings'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Customize your platform's appearance and branding.
            </Typography>

            <Grid container spacing={3}>
                {/* Logo Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Logo"
                            avatar={<UploadIcon />}
                            action={
                                (brandingSettings.customLogoBase64 || uploadedFile) && (
                                    <IconButton onClick={handleRemoveLogo} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                )
                            }
                        />
                        <CardContent>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Current Logo
                                </Typography>
                                <Box sx={{
                                    border: '1px dashed #ccc',
                                    p: 2,
                                    borderRadius: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    minHeight: 100
                                }}>
                                    {uploadedFile ? (
                                        <img
                                            src={URL.createObjectURL(uploadedFile)}
                                            alt="Uploaded logo preview"
                                            style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <Logo height={80} />
                                    )}
                                </Box>
                            </Box>

                            <ImageUpload
                                mime_type="image"
                                onSelect={handleImageSelect}
                                field_name="logo"
                                index={0}
                                selected={brandingSettings.customLogoBase64}
                                buttonProps={{
                                    label: "Upload New Logo",
                                    fullWidth: true,
                                    variant: "outlined"
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>

                {/* Organization Name Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Organization Details"
                            avatar={<TypographyIcon />}
                        />
                        <CardContent>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Organization Name"
                                    value={brandingSettings.organizationName || organization?.name || ""}
                                    onChange={handleOrganizationNameChange}
                                    placeholder="Enter organization name"
                                    variant="outlined"
                                    size="small"
                                    helperText="Full organization name"
                                />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    fullWidth
                                    label="Short Name"
                                    value={brandingSettings.organizationShortName || organization?.short_name || ""}
                                    onChange={handleOrganizationShortNameChange}
                                    placeholder="Enter short name (e.g., SMSP)"
                                    variant="outlined"
                                    size="small"
                                    helperText="Short name displayed in header"
                                />
                            </Box>
                            <Box>
                                <TextField
                                    fullWidth
                                    label="Logo URL"
                                    value={brandingSettings.logoUrl || organization?.logo || ""}
                                    onChange={handleLogoUrlChange}
                                    placeholder="Enter logo URL"
                                    variant="outlined"
                                    size="small"
                                    helperText="URL to organization logo image"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Theme & Font Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Theme & Typography"
                            avatar={<TypographyIcon />}
                        />
                        <CardContent>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Theme Mode
                                </Typography>
                                <ThemeSwitcher />
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Font Family
                                </Typography>
                                <FontSelector />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Primary Colors Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Primary Colors"
                            avatar={<PaletteIcon />}
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Light Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.light.primary.main}
                                        onChange={(e) => handleColorChange('palette.light.primary.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.light.primary.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                    {getContrastWarning('light') && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            {getContrastWarning('light')}
                                        </Alert>
                                    )}
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Dark Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.dark.primary.main}
                                        onChange={(e) => handleColorChange('palette.dark.primary.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.dark.primary.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                    {getContrastWarning('dark') && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            {getContrastWarning('dark')}
                                        </Alert>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Secondary Colors Section */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Secondary Colors"
                            avatar={<PaletteIcon />}
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Light Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.light.secondary.main}
                                        onChange={(e) => handleColorChange('palette.light.secondary.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.light.secondary.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Dark Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.dark.secondary.main}
                                        onChange={(e) => handleColorChange('palette.dark.secondary.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.dark.secondary.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Background Colors */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Background Colors"
                            subheader="Set the default background color for each theme"
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Light Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.light.background.default}
                                        onChange={(e) => handleColorChange('palette.light.background.default', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.light.background.default}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Dark Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.dark.background.default}
                                        onChange={(e) => handleColorChange('palette.dark.background.default', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.dark.background.default}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Paper Colors */}
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardHeader
                            title="Paper Colors"
                            subheader="Set the paper/surface color for cards and elevated elements"
                        />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Light Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.light.paper.main}
                                        onChange={(e) => handleColorChange('palette.light.paper.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.light.paper.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Dark Mode
                                    </Typography>
                                    <TextField
                                        type="color"
                                        value={brandingSettings.palette.dark.paper.main}
                                        onChange={(e) => handleColorChange('palette.dark.paper.main', e.target.value)}
                                        fullWidth
                                        size="small"
                                    />
                                    <Chip
                                        label={brandingSettings.palette.dark.paper.main}
                                        size="small"
                                        sx={{ mt: 1, width: '100%' }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleReset}
                                    color="warning"
                                >
                                    Reset to Defaults
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSave}
                                    color="primary"
                                >
                                    Save Settings
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
            >
                <Alert onClose={() => setSnackbarOpen(false)} severity="success">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BrandingSettings;
